import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';

const CalendarSelectionContext = createContext(null);

export const useCalendarSelection = () => {
  const context = useContext(CalendarSelectionContext);
  if (!context) {
    throw new Error('useCalendarSelection must be used within CalendarSelectionProvider');
  }
  return context;
};

export const CalendarSelectionProvider = ({
  children,
  multiSelect = false,
  maxSelections = 20,
  onSelectionChange,
}) => {
  const [selectedSlots, setSelectedSlots] = useState([]);
  const [selectionMode, setSelectionMode] = useState('single');

  const isSlotSelected = useCallback((slotId) => {
    return selectedSlots.some((slot) => slot.id === slotId);
  }, [selectedSlots]);

  const toggleSlot = useCallback((slot) => {
    setSelectedSlots((prev) => {
      const isSelected = prev.some((s) => s.id === slot.id);

      let newSelection;
      if (isSelected) {
        newSelection = prev.filter((s) => s.id !== slot.id);
      } else {
        if (!multiSelect) {
          newSelection = [slot];
        } else if (prev.length >= maxSelections) {
          return prev;
        } else {
          newSelection = [...prev, slot];
        }
      }

      if (onSelectionChange) {
        onSelectionChange(newSelection);
      }

      return newSelection;
    });
  }, [multiSelect, maxSelections, onSelectionChange]);

  const selectSlot = useCallback((slot) => {
    setSelectedSlots((prev) => {
      if (prev.some((s) => s.id === slot.id)) {
        return prev;
      }

      let newSelection;
      if (!multiSelect) {
        newSelection = [slot];
      } else if (prev.length >= maxSelections) {
        return prev;
      } else {
        newSelection = [...prev, slot];
      }

      if (onSelectionChange) {
        onSelectionChange(newSelection);
      }

      return newSelection;
    });
  }, [multiSelect, maxSelections, onSelectionChange]);

  const deselectSlot = useCallback((slotId) => {
    setSelectedSlots((prev) => {
      const newSelection = prev.filter((s) => s.id !== slotId);

      if (onSelectionChange) {
        onSelectionChange(newSelection);
      }

      return newSelection;
    });
  }, [onSelectionChange]);

  const clearSelection = useCallback(() => {
    setSelectedSlots([]);

    if (onSelectionChange) {
      onSelectionChange([]);
    }
  }, [onSelectionChange]);

  const selectMultiple = useCallback((slots) => {
    const slotsToAdd = multiSelect
      ? slots.slice(0, maxSelections)
      : slots.slice(0, 1);

    setSelectedSlots(slotsToAdd);

    if (onSelectionChange) {
      onSelectionChange(slotsToAdd);
    }
  }, [multiSelect, maxSelections, onSelectionChange]);

  const selectionCount = selectedSlots.length;
  const hasSelection = selectionCount > 0;
  const canSelectMore = multiSelect && selectionCount < maxSelections;

  const value = useMemo(() => ({
    selectedSlots,
    selectionMode,
    selectionCount,
    hasSelection,
    canSelectMore,
    maxSelections,
    multiSelect,

    isSlotSelected,
    toggleSlot,
    selectSlot,
    deselectSlot,
    clearSelection,
    selectMultiple,
    setSelectionMode,
  }), [
    selectedSlots,
    selectionMode,
    selectionCount,
    hasSelection,
    canSelectMore,
    maxSelections,
    multiSelect,
    isSlotSelected,
    toggleSlot,
    selectSlot,
    deselectSlot,
    clearSelection,
    selectMultiple,
  ]);

  return (
    <CalendarSelectionContext.Provider value={value}>
      {children}
    </CalendarSelectionContext.Provider>
  );
};

export default CalendarSelectionContext;
